import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo } from "@/seo/Seo";
import { PageHero } from "@/layouts/PageHero";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { CtaButton } from "@/components/common/CtaButton";
import { WorkflowTimeline } from "@/components/detail/WorkflowTimeline";
import { ServiceComparisonTable } from "@/components/detail/ComparisonTable";
import { FaqAccordion } from "@/components/detail/FaqAccordion";
import { DetailCta } from "@/components/detail/DetailCta";
import { TechStack } from "@/components/home/TechStack";
import { serviceDetails } from "@/data/serviceDetails";
import { DEV_WORKFLOW, SHARED_COMPARISON } from "@/data/detail.shared";
import { whyChooseUs } from "@/data/faq.data";
import { staggerContainer, fadeUp, viewportOnce } from "@/animations/motion";
import { ArrowUpRight, Check } from "@/icons";
import { siteConfig } from "@/config/site.config";
import { ServicesIntro } from "@/components/services/ServicesIntro";
import MarketingShowcase from "@/components/common/MarketingShowcase";
import servicesIllustration from "@/assets/images/marketing/services-hero.png";

const servicesFaqs = [
  { question: "Do you offer design, engineering, or both?", answer: "Both, under one roof. Many clients engage us end-to-end, but we're happy to slot in alongside your existing design or engineering team." },
  { question: "How do you price projects?", answer: "By scope. After a short discovery call we provide a clear estimate and the engagement model — fixed scope, retainer or team extension — that fits you best." },
  { question: "Can you work with our existing product?", answer: "Yes. We frequently extend, improve and maintain existing products and codebases, not just build from scratch." },
  { question: "Who will actually do the work?", answer: "Senior designers and engineers — the same people you meet in the sales process. No hand-offs to junior teams." },
  { question: "How quickly can we start?", answer: "Usually within one to two weeks. Book a free consultation and we'll map the fastest responsible path forward." },
  { question: "Do we own the code and IP?", answer: "Yes. You own the code, data and intellectual property, delivered with documentation for your team." },
];

export default function Services() {
  return (
    <>
      <Seo
        title="Services"
        description="Design and engineering services from VibeWell — websites, web & mobile apps, ERP, CRM, AI, automation, cloud and support."
        path="/services"
      />
      <PageHero
        eyebrow="Our Services"
        // titleLines={["Design and engineering,", "end to end."]}
         titleLines={[
          "Custom Software",
          "Development",
          "Services",
        ]}
        description="Helping businesses innovate, automate and grow through custom software, enterprise applications, AI-powered solutions, cloud technologies and digital transformation."
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <CtaButton to={siteConfig.primaryCta.path} withArrow magnetic>
            Book a Free Consultation
          </CtaButton>
          <CtaButton to="/solutions" variant="outline">
            Explore Solutions
          </CtaButton>
        </div>
      </PageHero>

      {/* <ServicesIntro /> */}
      <MarketingShowcase
        eyebrow="Technology Services"
        title="Software Engineered Around Your Business"
        description="
            We build scalable enterprise software, AI-powered automation,
            cloud-native applications and digital platforms that help
            organisations modernise operations, improve efficiency and
            accelerate business growth."
        bullets={[
            "AI-powered business automation",
            "Enterprise web & mobile applications",
            "Cloud-native scalable architectures",
            "Industry-specific digital transformation",
        ]}
        image={"/images/seo/transformation-portfolio-og.png"}
        imageAlt="VibeWell Technology Services"
        accent="#0F8B8D"
        primaryCta={{
            label: "Explore Services",
            href: "/contact",
        }}
        secondaryCta={{
            label: "View Portfolio",
            href: "/portfolio",
        }}
      />

      {/* Services overview grid */}
      <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="Our Services"
            title="Technology Solutions Designed Around Your Business"
            description="From AI-powered automation to enterprise software, we design and build digital solutions that help organisations innovate, automate and scale."
          />
          <motion.div
            variants={staggerContainer(0.05, 0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {serviceDetails.map((s) => (
              <motion.div key={s.slug} variants={fadeUp} data-testid={`services-card-${s.slug}`}>
                <Link
                  to={`/services/${s.slug}`}
                  className="group flex h-full flex-col rounded-[1.25rem] border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/20 hover:shadow-[0_24px_60px_-28px_rgba(15,118,110,0.35)]"
                >
                  <span
                    className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white"
                    style={{ background: s.accent }}
                  >
                    <s.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-semibold text-foreground">
                    {s.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {s.tagline}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-all duration-300 group-hover:gap-2.5">
                    Learn more <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Why custom development matters */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <SectionHeading
                eyebrow="Why custom"
                title="Software that fits your business — not the other way around"
                description="Off-the-shelf tools force you to compromise. Custom software adapts to how you actually work, removing friction and unlocking advantage."
              />
            </div>
            <ul className="grid gap-4 self-center">
              {whyChooseUs.slice(0, 4).map(({ title, description }) => (
                <li key={title} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <div>
                    <p className="font-heading text-sm font-semibold text-foreground">{title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </Section>

      {/* Development methodology */}
      <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="Methodology"
            title="A proven development process"
            description="Click any step to see how we work at each stage."
          />
          <div className="mt-10">
            <WorkflowTimeline nodes={DEV_WORKFLOW} />
          </div>
        </Container>
      </Section>

      {/* Technology expertise */}
      <div className="bg-card/40">
        <TechStack />
      </div>

      {/* Comparison */}
      <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="The difference"
            title="Spreadsheet vs generic vs custom"
            description="Why teams outgrow spreadsheets and generic tools — and what changes with custom software."
          />
          <div className="mt-10">
            <ServiceComparisonTable rows={SHARED_COMPARISON} />
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading eyebrow="FAQ" title="Frequently asked questions" align="center" />
          <div className="mt-10">
            <FaqAccordion faqs={servicesFaqs} />
          </div>
        </Container>
      </Section>

      <DetailCta />
    </>
  );
}
